import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgreementFileReadModel, useDeleteAgreementsByAgreementIdFilesAndFileIdMutation } from '@client/api';
import { Button, Modal, ModalFooter } from '@client/toolkit';
import { DeleteConfirmationModalContent } from '../../../../../components/delete-confirmation-modal-content/delete-confirmation-modal-content';

interface AgreementFileDeleteModalProps {
  agreementFile?: AgreementFileReadModel;
  open: boolean;
  onDeleteFulfilled: () => void;
  onCancel: () => void;
}

export const AgreementFileDeleteModal = ({
  agreementFile,
  open,
  onCancel,
  onDeleteFulfilled,
}: AgreementFileDeleteModalProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [mutation, { isLoading: isDeleting }] = useDeleteAgreementsByAgreementIdFilesAndFileIdMutation();

  const id = useMemo(() => agreementFile?.id, [agreementFile?.id]);

  const handleDelete = async () => {
    if (!agreementFile?.agreementId) return;
    if (!agreementFile?.file?.id) return;
    try {
      await mutation({ fileId: agreementFile?.file?.id, agreementId: agreementFile?.agreementId });
    } catch (e) {
      return;
    }
    onDeleteFulfilled();
  };

  const handleClose = () => {
    onCancel();
  };

  useEffect(() => {
    if (!open) return;
    if (id) return;
    onCancel();
  }, [onCancel, open, id]);

  return (
    <Modal open={open} onClose={handleClose} onBackdropClick={handleClose} closeDisabled={isDeleting}>
      <DeleteConfirmationModalContent name={tGlobal('entity.agreementFile')} />
      <ModalFooter className="justify-center">
        <Button color="danger" onClick={handleDelete} progressing={isDeleting} disabled={!id}>
          {tCommon('delete')}
        </Button>
        <Button color="primary-dark" onClick={handleClose}>
          {tCommon('cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
